import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, AlertTriangle, FileText, Tag, RefreshCw, Upload } from 'lucide-react'
import { api } from '../lib/api'
import { Card, CardContent, CardHeader } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Modal } from '../components/ui/Modal'
import { useActiveTenant } from '../contexts/tenant'
import { useAuth } from '../contexts/auth'

type OutboundItem = {
  id: string; productId: string; quantity: number
  locationId: string | null; palletId: string | null
  sku: string | null; productName: string | null; unitsPerBox: number | null
  locationCode: string | null
}
type Shipment = {
  provider: string; status: string; trackingCode: string | null; labelUrl: string | null
  authorizedAt: string | null; printedAt: string | null; postedAt: string | null
}
type Wave = { id: string; number: string; status: string; waveDate: string }
type Doc = { kind: string; source: string | null; byteSize: number; createdAt: string }
type Hist = { at: string; method: string; route: string; status: number; userEmail: string | null; userRole: string | null }
type OutboundOrder = {
  id: string; orderNumber: string; erpNumber: string | null; wsCode: string | null
  channel: string; originChannel: string | null; status: string
  customerName: string | null; customerDocument: string | null
  nfNumber: string | null; nfSeries: string | null; nfKey: string | null; nfValue: string | null
  emissionDate: string | null; carrier: string | null
  packagesCount: number; notes: string | null
  // Esteira manual / liberação sem etiqueta.
  modoEnvio: string | null; modoEnvioDetalhe: string | null
  liberadoSemEtiquetaEm: string | null; liberadoSemEtiquetaMotivo: string | null
  createdAt: string; shippedAt: string | null; confirmedAt: string | null
  items: OutboundItem[]; shipment: Shipment | null; wave: Wave | null
  docs: Doc[]; history: Hist[]
  // Diagnóstico: o que o pedido tem de fato, para o estado poder ser conferido.
  temBaixa?: boolean
  ondaSeparacao?: { reference: string | null; status: string } | null
  modoRegistro?: boolean
  alertas?: string[]
}

const statusLabel: Record<string, string> = {
  imported: 'Importado', awaiting_label: 'Aguardando etiqueta', pending: 'Pendente',
  reserved: 'Reservado', picking: 'Separando', packed: 'Embalado',
  shipped: 'Expedido', cancelled: 'Cancelado', returned: 'Devolvido',
}
const statusVariant: Record<string, 'default' | 'info' | 'warning' | 'success' | 'danger'> = {
  imported: 'default', awaiting_label: 'default', pending: 'default',
  reserved: 'info', picking: 'warning', packed: 'warning',
  shipped: 'success', cancelled: 'danger', returned: 'danger',
}
const nextStatus: Record<string, string> = { reserved: 'picking', picking: 'packed' }

// Nome legível para a rota registrada no audit_log — o histórico tem que ser lido
// por quem opera, não por quem escreveu a API.
const routeLabel: Record<string, string> = {
  'POST /outbound': 'Pedido criado',
  'PATCH /outbound/:id/status': 'Status alterado',
  'POST /outbound/:id/ship': 'Expedido',
  'POST /outbound/:id/reopen': 'Reaberto',
  'POST /outbound/:id/liberar-sem-etiqueta': 'Liberação sem etiqueta',
  'POST /outbound/:id/label': 'Etiqueta anexada',
  'POST /outbound/:id/label-zpl': 'Etiqueta (ZPL) anexada',
  'POST /outbound/recuperar-itens': 'Itens recuperados da NF',
  'POST /expedition/ship': 'Expedido (esteira)',
  'POST /expedition/ship-by-code': 'Expedido (bipagem)',
  'POST /expedition/ship-nfs': 'Expedido (lista de NFs)',
  'POST /expedition/confirm': 'Expedição confirmada',
  'POST /picking/waves': 'Entrou em onda de separação',
  'POST /separations/:id/baixa': 'Baixa da separação',
}

const fmt = (d?: string | null) => (d ? new Date(d).toLocaleString('pt-BR') : '—')

export function OutboundDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const tenantId = useActiveTenant()
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin'
  const [shipModal, setShipModal] = useState(false)
  const [shipForm, setShipForm] = useState({ nfNumber: '', nfKey: '', nfValue: '', packagesCount: '' })

  const { data: order, isLoading } = useQuery<OutboundOrder>({
    queryKey: ['outbound', id],
    queryFn: async () => (await api.get(`/outbound/${id}`)).data,
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: ['outbound', id] })

  const advance = useMutation({
    mutationFn: (status: string) => api.patch(`/outbound/${id}/status`, { status }),
    onSuccess: invalidate,
  })
  const cancel = useMutation({
    mutationFn: () => api.patch(`/outbound/${id}/status`, { status: 'cancelled' }),
    onSuccess: invalidate,
  })
  const ship = useMutation({
    mutationFn: () => api.post(`/outbound/${id}/ship`, {
      nfNumber: shipForm.nfNumber || undefined,
      nfKey: shipForm.nfKey || undefined,
      nfValue: shipForm.nfValue ? parseFloat(shipForm.nfValue) : undefined,
      packagesCount: shipForm.packagesCount ? parseInt(shipForm.packagesCount) : undefined,
    }),
    onSuccess: () => { setShipModal(false); invalidate() },
  })

  // Pedido de plataforma que vai sair sem etiqueta (cliente veio buscar): só
  // admin libera, com motivo, e ele vai em onda própria com declaração de
  // retirada. Sem isso o portão da onda o barra — que é o comportamento certo
  // para o caso comum.
  const liberar = useMutation({
    mutationFn: (motivo: string) => api.post(`/outbound/${id}/liberar-sem-etiqueta`, { motivo }),
    onSuccess: invalidate,
  })
  const desfazerLiberacao = useMutation({
    mutationFn: () => api.post(`/outbound/${id}/liberar-sem-etiqueta`, { desfazer: true }),
    onSuccess: invalidate,
  })
  function pedirLiberacao() {
    const motivo = window.prompt('Motivo da liberação sem etiqueta (fica registrado no pedido e na auditoria):')
    if (motivo == null) return
    if (motivo.trim().length < 5) { alert('Escreva o motivo (mínimo 5 caracteres).'); return }
    liberar.mutate(motivo.trim())
  }

  // Repõe os itens a partir da NF no ERP. Não mexe em estoque.
  const recuperar = useMutation({
    mutationFn: () => api.post('/outbound/recuperar-itens', { tenantId, orderIds: [id] }),
    onSuccess: invalidate,
  })

  // Anexo manual da etiqueta — a saída de quando o sistema não identifica.
  // O casamento automático (etiquetas em lote) erra em etiqueta sem chave
  // legível, NF ainda não sincronizada ou canal com layout novo; antes disso,
  // a única alternativa era recortar o PDF por fora. Aqui o arquivo vai direto
  // no pedido, PDF ou ZPL — o ZPL é renderizado para 10×15 no servidor.
  const [anexando, setAnexando] = useState(false)
  const [anexoMsg, setAnexoMsg] = useState<string | null>(null)
  async function anexarEtiqueta(f: File) {
    setAnexando(true); setAnexoMsg(null)
    try {
      const bytes = await f.arrayBuffer()
      const inicio = new TextDecoder('latin1').decode(bytes.slice(0, 512))
      if (inicio.startsWith('%PDF')) {
        // FileReader e não String.fromCharCode(...bytes): o spread de alguns MB
        // estoura a pilha do navegador e o erro não diz o porquê.
        const b64 = await new Promise<string>((res, rej) => {
          const fr = new FileReader()
          fr.onload = () => res(String(fr.result).split(',')[1])
          fr.onerror = () => rej(new Error('Falha ao ler o arquivo'))
          fr.readAsDataURL(f)
        })
        await api.post(`/outbound/${id}/label`, { fileBase64: b64 })
      } else if (inicio.includes('^XA')) {
        await api.post(`/outbound/${id}/label-zpl`, { zpl: new TextDecoder('utf-8').decode(bytes) })
      } else {
        setAnexoMsg('Arquivo não reconhecido: envie o PDF da etiqueta ou o arquivo ZPL (^XA).')
        return
      }
      setAnexoMsg('Etiqueta anexada. O pedido já conta como "com etiqueta" e entra em onda.')
      invalidate()
    } catch (e) {
      const err = e as { response?: { data?: { title?: string; message?: string } } }
      setAnexoMsg(err.response?.data?.title ?? err.response?.data?.message ?? 'Falha ao anexar a etiqueta')
    } finally { setAnexando(false) }
  }

  async function abrirPdf(tipo: 'danfe' | 'label') {
    const url = tipo === 'danfe' ? `/outbound/${id}/danfe` : `/outbound/${id}/label`
    const res = await api.get(url, { responseType: 'blob' })
    window.open(URL.createObjectURL(res.data as Blob), '_blank')
  }

  if (isLoading) return <p className="p-8 text-center text-sm text-gray-400">Carregando...</p>
  if (!order) return <p className="p-8 text-center text-sm text-gray-400">Pedido não encontrado.</p>

  const semItens = order.items.length === 0
  const totalUn = order.items.reduce((s, i) => s + i.quantity, 0)
  const temDanfe = order.docs.some(d => d.kind === 'danfe') || !!order.nfKey
  const temEtiqueta = order.docs.some(d => d.kind === 'label')

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}><ArrowLeft className="h-4 w-4" /></Button>
        <div className="flex-1">
          <h2 className="text-xl font-semibold text-gray-900">
            {order.wsCode ?? order.orderNumber}
          </h2>
          <p className="text-sm text-gray-500">
            {order.customerName ?? 'Sem destinatário'}
            {order.originChannel ? ` · ${order.originChannel}` : ''}
          </p>
        </div>
        <Badge variant={statusVariant[order.status] ?? 'default'}>{statusLabel[order.status] ?? order.status}</Badge>
      </div>

      {/* Como este pedido sai: etiqueta (plataforma) ou esteira manual / liberado. */}
      {(order.modoEnvio || order.liberadoSemEtiquetaEm || (order.channel !== 'bling' && order.channel !== 'olist')) && (
        <div className={`flex flex-wrap items-center gap-3 rounded-lg border p-3 text-sm ${order.liberadoSemEtiquetaEm ? 'border-amber-300 bg-amber-50 text-amber-900' : 'border-blue-200 bg-blue-50 text-blue-900'}`}>
          {order.liberadoSemEtiquetaEm ? (
            <span className="flex-1">
              <b>Liberado sem etiqueta</b> em {new Date(order.liberadoSemEtiquetaEm).toLocaleString('pt-BR')} — motivo: {order.liberadoSemEtiquetaMotivo ?? '—'}.
              Vai em onda própria (filtro “liberados” em Pedidos) e sai com declaração de retirada.
            </span>
          ) : (
            <span className="flex-1">
              <b>Esteira manual</b> · {({ retirada: 'Retirada no balcão', motoboy: 'Motoboy / entrega própria', externa: 'Transportadora contratada por fora', etiqueta: 'Etiqueta anexada' } as Record<string, string>)[order.modoEnvio ?? 'externa'] ?? order.modoEnvio}
              {order.modoEnvioDetalhe ? ` (${order.modoEnvioDetalhe})` : ''}
              {order.modoEnvio === 'etiqueta'
                ? (temEtiqueta ? ' · etiqueta anexada ✓' : ' · falta anexar a etiqueta para entrar em onda')
                : ' · sai sem etiqueta de transportadora, com declaração de retirada assinada'}
            </span>
          )}
          {isAdmin && order.liberadoSemEtiquetaEm && !['shipped', 'cancelled', 'returned'].includes(order.status) && (
            <Button size="sm" variant="secondary" loading={desfazerLiberacao.isPending} onClick={() => desfazerLiberacao.mutate()}>Desfazer liberação</Button>
          )}
        </div>
      )}
      {isAdmin && !order.liberadoSemEtiquetaEm && (order.channel === 'bling' || order.channel === 'olist')
        && !['shipped', 'cancelled', 'returned'].includes(order.status) && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
          <span className="flex-1">
            Pedido de plataforma: só entra em onda com etiqueta (Melhor Envio, anexo ou marketplace). Se vai sair sem etiqueta
            — cliente veio buscar, transportadora contratada por fora — libere aqui com o motivo; ele vai em onda própria.
          </span>
          <Button size="sm" variant="secondary" loading={liberar.isPending} onClick={pedirLiberacao}>Liberar sem etiqueta</Button>
          {liberar.error && <span className="text-xs text-red-600">{(liberar.error as any)?.response?.data?.title ?? 'Falha ao liberar'}</span>}
        </div>
      )}

      {/* O alerta que faltava: pedido sem produto não baixa estoque ao ser expedido. */}
      {semItens && (
        <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-red-800">Pedido sem produto</p>
            <p className="mt-1 text-sm text-red-700">
              A etiqueta sai com “0 un”, o relatório de produtos fica errado e a expedição
              não baixa estoque. {order.nfNumber
                ? 'A NF deste pedido existe — dá para repor os itens a partir dela.'
                : 'Sem NF ainda: os itens chegam quando a nota for autorizada.'}
            </p>
            {order.nfNumber && (
              <Button size="sm" className="mt-3" loading={recuperar.isPending} onClick={() => recuperar.mutate()}>
                <RefreshCw className="h-4 w-4" /> Recuperar itens da NF {order.nfNumber}
              </Button>
            )}
            {recuperar.data && (
              <p className="mt-2 text-sm text-red-800">
                {JSON.stringify((recuperar.data as { data: unknown }).data)}
              </p>
            )}
          </div>
        </div>
      )}

      {/* O que não fecha neste pedido. Estado que se contradiz precisa aparecer
          no topo: "Expedido" sozinho não conta a história de um pedido dado como
          enviado antes de a etiqueta existir. */}
      {!!order.alertas?.length && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4">
          <p className="mb-1 text-sm font-semibold text-amber-900">Este pedido não fecha</p>
          <ul className="list-disc space-y-1 pl-5 text-sm text-amber-800">
            {order.alertas.map((a, i) => <li key={i}>{a}</li>)}
          </ul>
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <span className="text-sm font-semibold text-gray-900">
              Produtos {!semItens && <span className="font-normal text-gray-500">· {order.items.length} itens · {totalUn} un</span>}
            </span>
          </CardHeader>
          <CardContent className="p-0">
            {semItens ? (
              <p className="px-5 py-8 text-center text-sm text-red-600">Nenhum produto neste pedido.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="border-b border-gray-100 bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">SKU</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">Produto</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-600">Local</th>
                    <th className="px-4 py-2 text-right font-medium text-gray-600">Qtd</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {order.items.map(it => (
                    <tr key={it.id}>
                      <td className="px-4 py-2 font-mono text-xs text-gray-500">{it.sku ?? '—'}</td>
                      <td className="px-4 py-2 text-gray-700">{it.productName ?? '—'}</td>
                      <td className="px-4 py-2 font-mono text-xs text-gray-500">{it.locationCode ?? 'GERAL'}</td>
                      <td className="px-4 py-2 text-right font-semibold">{it.quantity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><span className="text-sm font-semibold text-gray-900">Nota fiscal</span></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <Linha rotulo="Número" valor={order.nfNumber ? `${order.nfNumber}${order.nfSeries ? ` / ${order.nfSeries}` : ''}` : '—'} />
            <Linha rotulo="Valor" valor={order.nfValue ? `R$ ${order.nfValue}` : '—'} />
            <Linha rotulo="Emissão" valor={order.emissionDate ? new Date(order.emissionDate).toLocaleDateString('pt-BR') : '—'} />
            <Linha rotulo="Transportadora" valor={order.carrier ?? '—'} />
            {order.nfKey && <p className="break-all pt-1 font-mono text-[11px] text-gray-500">{order.nfKey}</p>}
            <div className="flex flex-wrap items-center gap-2 pt-2">
              {temDanfe && <Button variant="secondary" size="sm" onClick={() => abrirPdf('danfe')}><FileText className="h-4 w-4" /> DANFE</Button>}
              {temEtiqueta && <Button variant="secondary" size="sm" onClick={() => abrirPdf('label')}><Tag className="h-4 w-4" /> Etiqueta</Button>}
              {/* Sempre disponível: é a saída quando o casamento automático não
                  identifica a etiqueta. Com etiqueta já anexada, substitui. */}
              <label className={`inline-flex cursor-pointer items-center gap-2 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 ${anexando ? 'pointer-events-none opacity-60' : ''}`}>
                <Upload className="h-4 w-4" />
                {anexando ? 'anexando…' : temEtiqueta ? 'Substituir etiqueta' : 'Anexar etiqueta'}
                <input type="file" accept=".pdf,.zpl,.txt" className="hidden"
                  onChange={e => { const f = e.target.files?.[0]; if (f) anexarEtiqueta(f); e.currentTarget.value = '' }} />
              </label>
            </div>
            {anexoMsg && <p className="pt-1 text-xs text-gray-600">{anexoMsg}</p>}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <Card>
          <CardHeader><span className="text-sm font-semibold text-gray-900">Pedido</span></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <Linha rotulo="Nº do pedido" valor={order.orderNumber} />
            <Linha rotulo="Nº no ERP" valor={order.erpNumber ?? '—'} />
            <Linha rotulo="Canal" valor={order.originChannel ?? order.channel} />
            <Linha rotulo="Destinatário" valor={order.customerName ?? '—'} />
            <Linha rotulo="CPF/CNPJ" valor={order.customerDocument ?? '—'} />
            <Linha rotulo="Volumes" valor={String(order.packagesCount)} />
            <Linha rotulo="Criado em" valor={fmt(order.createdAt)} />
            <Linha rotulo="Expedido em" valor={fmt(order.shippedAt)} />
            {order.notes && <Linha rotulo="Obs." valor={order.notes} />}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><span className="text-sm font-semibold text-gray-900">Envio e separação</span></CardHeader>
          <CardContent className="space-y-2 text-sm">
            {order.shipment ? (
              <>
                <Linha rotulo="Rastreio" valor={order.shipment.trackingCode ?? '—'} />
                <Linha rotulo="Etiqueta" valor={order.shipment.status} />
                <Linha rotulo="Autorizada" valor={fmt(order.shipment.authorizedAt)} />
                <Linha rotulo="Postada" valor={fmt(order.shipment.postedAt)} />
              </>
            ) : (
              <p className="text-gray-400">Sem envio casado no Melhor Envio.</p>
            )}
            {order.wave ? (
              <div className="pt-2">
                <Linha rotulo="Onda" valor={`${order.wave.number} · ${order.wave.status}`} />
                <Button variant="ghost" size="sm" className="mt-1" onClick={() => navigate(`/picking/${order.wave!.id}`)}>
                  Abrir onda
                </Button>
              </div>
            ) : (
              <p className="pt-2 text-gray-400">Não entrou em onda de separação.</p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <span className="text-sm font-semibold text-gray-900">Histórico</span>
          <div className="flex gap-2">
            {nextStatus[order.status] && (
              <Button size="sm" variant="secondary" loading={advance.isPending}
                onClick={() => advance.mutate(nextStatus[order.status])}>
                Avançar para {statusLabel[nextStatus[order.status]]}
              </Button>
            )}
            {order.status === 'packed' && <Button size="sm" onClick={() => setShipModal(true)}>Expedir</Button>}
            {order.status !== 'shipped' && order.status !== 'cancelled' && (
              <Button size="sm" variant="ghost" loading={cancel.isPending} onClick={() => cancel.mutate()}>Cancelar</Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {order.history.length === 0 ? (
            <p className="px-5 py-8 text-center text-sm text-gray-400">Sem registro de alterações.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-gray-100 bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-gray-600">Quando</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-600">O quê</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-600">Quem</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-600">HTTP</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {order.history.map((h, i) => (
                  <tr key={i} className={h.status >= 400 ? 'bg-red-50' : ''}>
                    <td className="px-4 py-2 whitespace-nowrap text-gray-500">{fmt(h.at)}</td>
                    <td className="px-4 py-2 text-gray-700">
                      {routeLabel[`${h.method} ${h.route}`] ?? `${h.method} ${h.route}`}
                    </td>
                    <td className="px-4 py-2 text-gray-500">{h.userEmail ?? h.userRole ?? '—'}</td>
                    <td className={`px-4 py-2 text-right font-mono text-xs ${h.status >= 400 ? 'text-red-600' : 'text-gray-400'}`}>{h.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Modal title="Expedir pedido" open={shipModal} onClose={() => setShipModal(false)}>
        <div className="space-y-3">
          <Input label="Número da NF" value={shipForm.nfNumber} onChange={e => setShipForm(p => ({ ...p, nfNumber: e.target.value }))} />
          <Input label="Chave da NF" value={shipForm.nfKey} onChange={e => setShipForm(p => ({ ...p, nfKey: e.target.value }))} />
          <Input label="Valor da NF" type="number" value={shipForm.nfValue} onChange={e => setShipForm(p => ({ ...p, nfValue: e.target.value }))} />
          <Input label="Volumes" type="number" value={shipForm.packagesCount} onChange={e => setShipForm(p => ({ ...p, packagesCount: e.target.value }))} />
          {ship.error && (
            <p className="text-sm text-red-600">
              {(ship.error as { response?: { data?: { title?: string } } })?.response?.data?.title ?? 'Erro ao expedir'}
            </p>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="secondary" onClick={() => setShipModal(false)}>Cancelar</Button>
            <Button loading={ship.isPending} onClick={() => ship.mutate()}>Confirmar expedição</Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

function Linha({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-gray-500">{rotulo}</span>
      <span className="text-right font-medium text-gray-800">{valor}</span>
    </div>
  )
}
